import { useMemo } from 'react';
import equal from 'fast-deep-equal';
import { useProductInventoryByHandle } from '@backpackjs/storefront';

import { ColorOptionIcon } from './ColorOptionIcon';
import { Link } from '../../../../../snippets';

export function ColorOptionLink({
  groupingProductsMapByColor,
  isSelected,
  newSelectedOptions,
  swatch,
  value,
  disableGroupHistory,
  enabledSoldOutColorSwatch,
  soldOutQty,
  needsSelection,
  optionChanges,
  groupedBreadcrumbs,
}) {
  const selectedVariantFromOptions = useMemo(() => {
    if (!groupingProductsMapByColor || !newSelectedOptions) return null;

    const variants = groupingProductsMapByColor[newSelectedOptions.Color]?.variants;
    if (!variants) return null;

    let bestMatch = null;
    let maxMatches = -1;

    for (const variant of variants) {
        const { selectedOptionsMap } = variant;
        const matches = Object.keys(newSelectedOptions).reduce((count, key) => {
            return count + (newSelectedOptions[key] === selectedOptionsMap[key] ? 1 : 0);
        }, 0);

        if (matches >= maxMatches) {
            maxMatches = matches;
            bestMatch = variant;
        }
    }

    return bestMatch;
}, [groupingProductsMapByColor, newSelectedOptions]);

  const numberOfProperties = Object.keys(newSelectedOptions || {}).length;
  const numberOfPropertiesChanged = Object.keys(optionChanges || {}).length;

  const url = useMemo(() => {
    if (!selectedVariantFromOptions) return null;

    let params = '';
    if (typeof window !== 'undefined') {
      const { search } = window.location;
      params = new URLSearchParams(search);
      params.set('variant', selectedVariantFromOptions.legacyResourceId);
      if (!needsSelection) {
        params.set('s', needsSelection);
      } else {
        if (numberOfProperties > 2 && numberOfPropertiesChanged > 1) {
          Object.entries(optionChanges).forEach(([key, value]) => {
            if (key !== 'Color') {
              params.set(key, value.toString());
            }
          });
        }
      }
    }

    return `/products/${selectedVariantFromOptions.product.handle}?${params}`;
  }, [selectedVariantFromOptions, needsSelection, optionChanges, numberOfPropertiesChanged]);

  const { inventory, success: inventoryFetched } = useProductInventoryByHandle({
    handle: selectedVariantFromOptions?.product?.handle,
    withInventory: false,
  });

  const groupIsLoading = !inventoryFetched;

  const totalQuantityAvailable = Object.values(inventory?.variants || {}).reduce((total, variant) => total + variant.quantityAvailable, 0);
  const isAllOutOfStock = totalQuantityAvailable <= soldOutQty;
  const optionValueIsAvailable =
    totalQuantityAvailable <= soldOutQty
      ? false
      : inventory?.variants?.[selectedVariantFromOptions?.id]
          ?.availableForSale || false;
  const disabled = !inventoryFetched || !selectedVariantFromOptions;
  const isUnavailable = !optionValueIsAvailable && !disabled && !needsSelection;

  return (
    <Link
      aria-label={`${value}${isAllOutOfStock || isUnavailable ? ' is Sold Out' : ''}`}
      className="group/color"
      href={url}
      onClick={(e) => {
        if (isSelected) e.preventDefault();
        if (groupedBreadcrumbs?.title && groupedBreadcrumbs?.handle) {
          sessionStorage.setItem('savedCollectionTitle', groupedBreadcrumbs?.title);
          sessionStorage.setItem('savedCollectionHandle', groupedBreadcrumbs?.handle);
          sessionStorage.setItem('savedHistory', groupedBreadcrumbs?.path);
        }
      }}
      scroll={false}
      replace={disableGroupHistory ? true : false}
    >
      <ColorOptionIcon
        isLoading={groupIsLoading}
        isUnavailable={isUnavailable}
        isSelected={isSelected}
        swatch={swatch}
        value={value}
        enabledSoldOutColorSwatch={enabledSoldOutColorSwatch}
        isAllOutOfStock={isAllOutOfStock}
      />
    </Link>
  );
}

ColorOptionLink.displayName = 'ColorOptionLink';
